'use client';

import { hasValue } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import type { Accept, DropzoneOptions } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { BasicPlus } from '../../../icons/basic';
import { Typography } from '../../typography/Typography';
import { useFormField } from '../FormField';
import { acceptImages } from './acceptFileTypes';
import type { MaskedDocumentUploadTexts } from './maskedDocumentUploadTexts';
import { resolveMaskedDocumentUploadTexts } from './maskedDocumentUploadTexts';
import { buildNextCommittedFiles } from './maskedDocumentUploadUtils';
import { BatchFile, MaskingDialog } from './MaskingDialog';
import { burnRedactions } from './upload-shared/burnRedactions';
import { filesMatch, getFileKey } from './upload-shared/fileIdentity';
import type { RedactionRect } from './upload-shared/redactionRect';
import { resizeForEditing } from './upload-shared/resizeForEditing';
import { UploadedFile } from './upload-shared/UploadedFile';
import { watermarkImage } from './upload-shared/watermarkImage';

type Props = {
  accept?: Accept;
  maxFiles?: number;
  capture?: boolean | 'user' | 'environment';
  transformFiles?: (files: File[]) => Promise<File[]>;
  /** @deprecated Use `texts.chooseFile` instead */
  chooseFileText?: string;
  /** @deprecated Use `texts.dropFile` instead */
  dropFileText?: string;
  maskingInstructions?: ReactNode;
  /** @deprecated Use `texts.confirmButton` instead */
  confirmButtonText?: string;
  /** @deprecated Use `texts.rejectButton` instead */
  rejectButtonText?: string;
  texts?: Partial<MaskedDocumentUploadTexts>;
  showPreview?: boolean;
  watermarkPurpose?: string;
};

function findNextPendingIndex(batchFiles: BatchFile[], startIndex = 0): number {
  const index = batchFiles.findIndex((item, i) => i >= startIndex && item.status === 'pending');

  if (index !== -1) {
    return index;
  }

  return batchFiles.findIndex((item) => item.status === 'pending');
}

export const MaskedDocumentUpload = React.forwardRef<HTMLDivElement, Props>(({
  accept = acceptImages,
  maxFiles = 1,
  capture,
  transformFiles,
  chooseFileText,
  dropFileText,
  maskingInstructions,
  confirmButtonText,
  rejectButtonText,
  texts,
  showPreview = true,
  watermarkPurpose,
}, ref) => {
  const resolvedTexts = resolveMaskedDocumentUploadTexts({
    ...texts,
    ...(chooseFileText ? { chooseFile: chooseFileText } : {}),
    ...(dropFileText ? { dropFile: dropFileText } : {}),
    ...(confirmButtonText ? { confirmButton: confirmButtonText } : {}),
    ...(rejectButtonText ? { rejectButton: rejectButtonText } : {}),
  });

  const {
    field,
    fieldState,
    name,
    setValue,
  } = useFormField();

  const confirmedFiles = useMemo(
    () => (Array.isArray(field?.value)
      ? field.value.filter((file: unknown): file is File => file instanceof File)
      : []),
    [field?.value],
  );

  const [batchFiles, setBatchFiles] = useState<BatchFile[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const isCommittingRef = useRef(false);

  const remainingSlots = maxFiles - confirmedFiles.length;
  const isDropzoneDisabled = field?.disabled || remainingSlots <= 0 || dialogOpen;

  const applyWatermarkIfNeeded = useCallback(async (
    file: File,
    applyWatermark: boolean,
  ): Promise<File> => {
    if (!applyWatermark || !watermarkPurpose) {
      return file;
    }

    try {
      return await watermarkImage(file, { purpose: watermarkPurpose });
    } catch (e) {
      console.error('Watermark failed, using original file:', e);

      return file;
    }
  }, [watermarkPurpose]);

  const commitFiles = useCallback(async (
    files: File[],
    options: { applyWatermark?: boolean } = {},
  ) => {
    const { applyWatermark = true } = options;

    if (files.length === 0 || isCommittingRef.current) {
      return;
    }

    isCommittingRef.current = true;

    try {
      const processedFiles = await Promise.all(
        files.map((file) => applyWatermarkIfNeeded(file, applyWatermark)),
      );

      const currentFiles = Array.isArray(field?.value)
        ? field.value.filter((value: unknown): value is File => value instanceof File)
        : [];

      const nextValue = buildNextCommittedFiles(currentFiles, processedFiles, maxFiles);

      setValue(name, nextValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } finally {
      isCommittingRef.current = false;
    }
  }, [applyWatermarkIfNeeded, field?.value, maxFiles, name, setValue]);

  const commitFile = useCallback(async (
    file: File,
    options: { applyWatermark?: boolean } = {},
  ) => {
    await commitFiles([file], options);
  }, [commitFiles]);

  const removeConfirmedFile = useCallback((file: File) => {
    const nextValue = confirmedFiles.filter((confirmedFile) => !filesMatch(confirmedFile, file));

    setValue(name, nextValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [confirmedFiles, name, setValue]);

  const openMaskingDialog = useCallback((files: BatchFile[]) => {
    setBatchFiles(files);
    setActiveIndex(findNextPendingIndex(files));
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setBatchFiles([]);
    setActiveIndex(0);
  }, []);

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles) => {
    if (remainingSlots <= 0) {
      return;
    }

    const filesToProcess = acceptedFiles.slice(0, remainingSlots);

    const transformedFiles = await Promise.all(filesToProcess.map(async (file) => {
      let processed: File = file;

      if (hasValue(transformFiles)) {
        const transformedFile = (await transformFiles([file]))[0];

        if (hasValue(transformedFile)) {
          processed = transformedFile;
        }
      }

      return resizeForEditing(processed);
    }));

    const validFiles = transformedFiles.filter((file): file is File => file instanceof File);

    if (validFiles.length === 0) {
      return;
    }

    const newBatch: BatchFile[] = validFiles.map((file) => ({
      file,
      status: 'pending',
    }));

    openMaskingDialog(newBatch);
  };

  const handleConfirm = async (rectangles: RedactionRect[]) => {
    const currentBatchFile = batchFiles[activeIndex];

    if (!currentBatchFile) {
      return;
    }

    let updatedBatch = batchFiles;

    if (currentBatchFile.status === 'pending') {
      let fileToCommit = currentBatchFile.file;

      if (rectangles.length > 0) {
        try {
          fileToCommit = await burnRedactions(currentBatchFile.file, rectangles);
        } catch (e) {
          console.error('Burn redactions failed, using original file:', e);
        }
      }

      await commitFile(fileToCommit);

      updatedBatch = batchFiles.map((item, index) => (
        index === activeIndex ? { ...item, status: 'confirmed' as const } : item
      ));
    }

    const nextPendingIndex = findNextPendingIndex(updatedBatch, activeIndex + 1);

    if (nextPendingIndex === -1) {
      closeDialog();

      return;
    }

    setBatchFiles(updatedBatch);
    setActiveIndex(nextPendingIndex);
  };

  const handleConfirmAll = async () => {
    const pendingFiles = batchFiles
      .filter((batchFile) => batchFile.status === 'pending')
      .map((batchFile) => batchFile.file);

    if (pendingFiles.length === 0) {
      closeDialog();

      return;
    }

    await commitFiles(pendingFiles);
    closeDialog();
  };

  const handleDeleteReviewFile = (file: File) => {
    removeConfirmedFile(file);

    const nextCount = confirmedFiles.length - 1;

    if (nextCount <= 0) {
      closeDialog();

      return;
    }

    setActiveIndex((currentIndex) => Math.min(currentIndex, nextCount - 1));
  };

  const handleDeleteConfirmed = (file: File) => {
    removeConfirmedFile(file);
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isFileDialogActive,
  } = useDropzone({
    accept,
    multiple: maxFiles > 1,
    maxFiles: remainingSlots,
    disabled: isDropzoneDisabled,
    onDrop,
  });

  const isActive = isFileDialogActive || isDragActive;

  const rootClasses = clsx('relative flex h-14 w-full', {
    'bg-upload-enabled border border-dashed border-upload-enabled text-upload-enabled cursor-pointer hover:border-solid':
      !isDropzoneDisabled && !fieldState.error && !isActive,
    'bg-upload-dragging border-2 border-solid border-upload-dragging text-upload-dragging cursor-pointer':
      !isDropzoneDisabled && !fieldState.error && isActive,
    'bg-upload-error border border-upload-error text-upload-error': fieldState.error,
    'bg-upload-disabled text-upload-disabled': isDropzoneDisabled,
  });

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {confirmedFiles.map((file, index) => (
        <UploadedFile
          key={getFileKey(file)}
          error={Array.isArray(fieldState.error) ? fieldState.error[index] : undefined}
          onDelete={handleDeleteConfirmed}
          file={file}
          showPreview={showPreview}
        />
      ))}

      {remainingSlots > 0 && (
        <div
          className={rootClasses}
          {...(isDropzoneDisabled ? {} : getRootProps())}
          ref={ref}
        >
          {!isDropzoneDisabled && (
            <input
              {...getInputProps({
                name,
                capture,
                style: {
                  border: 0,
                  opacity: 0,
                  display: 'block',
                  height: '100%',
                  padding: '0px',
                  cursor: 'pointer',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  whiteSpace: 'nowrap',
                },
              })}
            />
          )}
          <div className="pointer-events-none flex w-full items-center justify-center">
            {isDragActive && (
              <Typography variant="content-body-bold">
                {resolvedTexts.dropFile}
              </Typography>
            )}
            {!isDragActive && (
              <>
                <BasicPlus className="mr-3 size-4" />
                <Typography variant="label-regular">
                  {resolvedTexts.chooseFile}
                </Typography>
              </>
            )}
          </div>
        </div>
      )}

      <MaskingDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        mode="masking"
        batchFiles={batchFiles}
        reviewFiles={confirmedFiles}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        onConfirm={handleConfirm}
        onConfirmAll={handleConfirmAll}
        onDeleteReviewFile={handleDeleteReviewFile}
        onClose={closeDialog}
        maskingInstructions={maskingInstructions}
        hasWatermark={!!watermarkPurpose}
        watermarkPurpose={watermarkPurpose}
        texts={resolvedTexts}
      />
    </div>
  );
});

MaskedDocumentUpload.displayName = 'MaskedDocumentUpload';
